/**
 * User Signature History Component
 *
 * Displays all e-signatures created by a specific user
 * Shows signature details, validity, and audit trail
 */

import React, { useState, useEffect } from 'react';
import { Modal, Table, Tag, Button, Space, Descriptions, Tabs, Badge } from 'antd';
import {
  SafetyCertificateOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  HistoryOutlined,
  FileTextOutlined
} from '@ant-design/icons';
import Axios from 'axios';
import moment from 'moment';

const UserSignatureHistory = ({ visible, onClose, userId, username }) => {
  const [signatures, setSignatures] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedSignature, setSelectedSignature] = useState(null);
  const [auditTrail, setAuditTrail] = useState([]);

  useEffect(() => {
    if (visible && userId) {
      loadUserSignatures();
    }
  }, [visible, userId]);

  const loadUserSignatures = async () => {
    try {
      setLoading(true);
      // Pass employee_id to get signatures
      const response = await Axios.get('/get_user_signatures', {
        params: { employee_id: userId },
        headers: {
          Authorization: `Bearer ${localStorage.getItem('Token')}`
        }
      });

      if (response.data.status === 'success') {
        setSignatures(response.data.data || []);
      } else {
        setSignatures([]);
      }
    } catch (error) {
      console.error('Error loading signatures:', error);
      setSignatures([]);
    } finally {
      setLoading(false);
    }
  };

  const loadAuditTrail = async (signatureId) => {
    try {
      const response = await Axios.get('/get_signature_audit_trail', {
        params: { signature_id: signatureId },
        headers: {
          Authorization: `Bearer ${localStorage.getItem('Token')}`
        }
      });

      if (response.data.status === 'success') {
        setAuditTrail(response.data.data.audit_trail);
      }
    } catch (error) {
      console.error('Error loading audit trail:', error);
      setAuditTrail([]);
    }
  };

  const handleViewDetails = (signature) => {
    setSelectedSignature(signature);
    loadAuditTrail(signature.signature_id);
  };

  const columns = [
    {
      title: 'Date/Time',
      dataIndex: 'signed_date',
      key: 'signed_date',
      render: (date) => moment(date).format('DD-MMM-YYYY HH:mm:ss'),
      sorter: (a, b) => moment(a.signed_date).unix() - moment(b.signed_date).unix(),
      defaultSortOrder: 'descend'
    },
    {
      title: 'Document',
      dataIndex: 'document_number',
      key: 'document_number',
      render: (text, record) => (
        <div>
          <div>{text}</div>
          <div style={{ fontSize: 11, color: '#999' }}>
            {record.document_type}
          </div>
        </div>
      )
    },
    {
      title: 'Action',
      dataIndex: 'signature_meaning',
      key: 'signature_meaning'
    },
    {
      title: 'Signature Type',
      dataIndex: 'signature_type',
      key: 'signature_type',
      render: (type) => <Tag>{type}</Tag>
    },
    {
      title: 'Status',
      dataIndex: 'is_valid',
      key: 'is_valid',
      render: (valid) => (
        <Tag color={valid ? 'success' : 'error'} icon={valid ? <CheckCircleOutlined /> : <CloseCircleOutlined />}>
          {valid ? 'Valid' : 'Invalid'}
        </Tag>
      ),
      filters: [
        { text: 'Valid', value: true },
        { text: 'Invalid', value: false }
      ],
      onFilter: (value, record) => record.is_valid === value
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Button
          type="link"
          size="small"
          onClick={() => handleViewDetails(record)}
        >
          View Details
        </Button>
      )
    }
  ];

  const auditColumns = [
    {
      title: 'Event',
      dataIndex: 'event_type',
      key: 'event_type',
      render: (type) => <Tag color="blue">{type}</Tag>
    },
    {
      title: 'Timestamp',
      dataIndex: 'event_timestamp',
      key: 'event_timestamp',
      render: (date) => moment(date).format('DD-MMM-YYYY HH:mm:ss')
    },
    {
      title: 'Performed By',
      dataIndex: 'performed_by',
      key: 'performed_by'
    },
    {
      title: 'IP Address',
      dataIndex: 'ip_address',
      key: 'ip_address'
    }
  ];

  const getSignatureStats = () => {
    const total = signatures.length;
    const valid = signatures.filter(s => s.is_valid).length;
    const invalid = total - valid;

    return { total, valid, invalid };
  };

  const stats = getSignatureStats();

  return (
    <Modal
      title={
        <Space>
          <HistoryOutlined style={{ color: '#1890ff' }} />
          E-Signature History - {username}
        </Space>
      }
      visible={visible}
      onCancel={onClose}
      width={1000}
      footer={[
        <Button key="close" onClick={onClose}>
          Close
        </Button>
      ]}
    >
      <Space style={{ marginBottom: 16 }}>
        <Badge count={stats.total} showZero>
          <Tag color="blue">Total Signatures</Tag>
        </Badge>
        <Badge count={stats.valid} showZero>
          <Tag color="success">Valid</Tag>
        </Badge>
        <Badge count={stats.invalid} showZero>
          <Tag color="error">Invalid</Tag>
        </Badge>
      </Space>

      <Table
        dataSource={signatures}
        columns={columns}
        rowKey="signature_id"
        loading={loading}
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          showTotal: (total) => `Total ${total} signatures`
        }}
        size="small"
      />

      {/* Signature Details Modal */}
      <Modal
        title="Signature Details"
        visible={!!selectedSignature}
        onCancel={() => setSelectedSignature(null)}
        width={800}
        footer={[
          <Button key="close" onClick={() => setSelectedSignature(null)}>
            Close
          </Button>
        ]}
      >
        {selectedSignature && (
          <Tabs defaultActiveKey="1">
            <Tabs.TabPane
              tab={
                <span>
                  <FileTextOutlined />
                  Signature Info
                </span>
              }
              key="1"
            >
              <Descriptions bordered column={2} size="small">
                <Descriptions.Item label="Signature ID" span={2}>
                  <code>{selectedSignature.signature_id}</code>
                </Descriptions.Item>
                <Descriptions.Item label="Signed By">
                  {selectedSignature.signed_by}
                </Descriptions.Item>
                <Descriptions.Item label="Role">
                  {selectedSignature.role}
                </Descriptions.Item>
                <Descriptions.Item label="Date/Time" span={2}>
                  {moment(selectedSignature.signed_date).format('DD-MMM-YYYY HH:mm:ss')}
                </Descriptions.Item>
                <Descriptions.Item label="Document Type">
                  {selectedSignature.document_type}
                </Descriptions.Item>
                <Descriptions.Item label="Document Number">
                  {selectedSignature.document_number}
                </Descriptions.Item>
                <Descriptions.Item label="Signature Meaning" span={2}>
                  {selectedSignature.signature_meaning}
                </Descriptions.Item>
                <Descriptions.Item label="Reason" span={2}>
                  {selectedSignature.reason}
                </Descriptions.Item>
                <Descriptions.Item label="Status" span={2}>
                  <Tag color={selectedSignature.is_valid ? 'success' : 'error'}>
                    {selectedSignature.is_valid ? 'Valid' : 'Invalid'}
                  </Tag>
                </Descriptions.Item>
              </Descriptions>
            </Tabs.TabPane>

            <Tabs.TabPane
              tab={
                <span>
                  <HistoryOutlined />
                  Audit Trail
                </span>
              }
              key="2"
            >
              <Table
                dataSource={auditTrail}
                columns={auditColumns}
                rowKey="event_timestamp"
                size="small"
                pagination={false}
              />
            </Tabs.TabPane>
          </Tabs>
        )}
      </Modal>
    </Modal>
  );
};

export default UserSignatureHistory;
